// Story 11.1 — Configuración global de Monaco para que (a) los web workers
// resuelvan local-server (sin CDN ni `Failed to fetch worker` en self-host),
// (b) `monaco-yaml` esté wireado con el JSON Schema del DSL para autocomplete
// + hover + validación schema (AC #5), (c) solo se carguen `yaml` y
// `typescript` para no shippear los 50+ lenguajes de Monaco (~3MB → ~1MB).
//
// Resuelve P-330 (monaco-yaml stub) + P-331 (workers no configurados) +
// P-332 (bundle size) del code review de Story 11.1.

// `?worker` import → Vite genera chunks separados servidos local. Sin esto,
// Monaco intenta cargar workers desde un CDN configurable (default `cdn.jsdelivr`),
// rompiendo en deployments self-hosted detrás de firewall.
import EditorWorker from 'monaco-editor/esm/vs/editor/editor.worker?worker';
import { configureMonacoYaml } from 'monaco-yaml';
import YamlWorker from 'monaco-yaml/yaml.worker?worker';

// Lazy import del schema del DSL — evita cargar core entero antes de tiempo.
type Monaco = typeof import('monaco-editor');

let installed = false;

/**
 * Idempotente: setea workers + tema + monaco-yaml. Llamar antes (o desde) el
 * primer `<Editor onMount>`. Safe to call N veces — solo aplica una vez.
 */
export async function setupMonaco(monaco: Monaco): Promise<void> {
  if (installed) return;
  installed = true;

  // P-331: workers locales (sin CDN). El TS check sobre `self.MonacoEnvironment`
  // se relaja porque el tipo no está exportado por monaco-editor.
  const w = self as unknown as {
    MonacoEnvironment?: { getWorker: (workerId: string, label: string) => Worker };
  };
  w.MonacoEnvironment = {
    getWorker(_workerId: string, label: string) {
      if (label === 'yaml') return new YamlWorker();
      return new EditorWorker();
    },
  };

  // P-330: wirea monaco-yaml con el JSON Schema del DSL. Importamos el
  // schema lazy para que no contribuya al bundle inicial — solo se carga
  // cuando el editor monta.
  try {
    // Subexport browser-safe (igual que DeployDialog/wizard) — NO el barrel
    // root, que arrastra resolver/typescript-loader/emitter (node:fs/path/events)
    // y rompe el build de Vite.
    const core = await import('@revelation-bots/core/validator-browser');
    const schema = (core as unknown as { botDslSchema?: object }).botDslSchema;
    if (schema) {
      configureMonacoYaml(monaco, {
        enableSchemaRequest: false,
        hover: true,
        completion: true,
        validate: true,
        format: false,
        schemas: [
          {
            uri: 'https://revelation-bots/dsl.schema.json',
            // Match SOLO los archivos del DSL del bot (no ext/*.ts).
            fileMatch: ['*.yaml', '*.yml'],
            schema,
          },
        ],
      });
    } else {
      // Si el core no exporta `botDslSchema` todavía, fallamos silencioso —
      // Monaco sigue funcionando sin schema (validación caerá al backend
      // post-save). No tira un error para no romper el editor.
      console.warn(
        '[MonacoSetup] @revelation-bots/core no exporta `botDslSchema`; ' +
          'monaco-yaml se inicializa sin schema. Autocomplete YAML será básico.',
      );
    }
  } catch (err) {
    console.warn('[MonacoSetup] No se pudo cargar el DSL schema:', err);
  }
}
